import {
  AuthorizationSet,
  COMPANY_ID_PATH_REGEX,
  COMPANY_SELECT_ALLOWED_SUBDOMAIN_TOKENS,
  getCompanySubDomain,
  getCurrentRouterPathname,
  isLocalOrJenkinsMode,
  isReservedNonCompanySubDomain,
  isValidAppPath,
  localStorageHelpers,
  ProfileStorage,
} from "@ryde/shared";

/**
 * Rehydrates a stored company scope against the current profile's auth sets.
 * Falls back from an exact userRole id match to company+role, then to a single
 * role for the stored company — covers duplicate-user rows that share a phone.
 */
export const findRestoredAuthSet = ({
  authSets,
  stored,
}: {
  authSets: AuthorizationSet[];
  stored: AuthorizationSet | undefined;
}): AuthorizationSet | undefined => {
  if (!stored) {
    return undefined;
  }

  const exactMatch = authSets.find(
    authSet =>
      authSet.id === stored.id && authSet.companyId === stored.companyId,
  );

  if (exactMatch) {
    return exactMatch;
  }

  const companyAndRoleMatch = authSets.find(
    authSet =>
      authSet.companyId === stored.companyId &&
      authSet.roleId === stored.roleId,
  );

  if (companyAndRoleMatch) {
    return companyAndRoleMatch;
  }

  const setsForStoredCompany = authSets.filter(
    authSet => authSet.companyId === stored.companyId,
  );

  if (setsForStoredCompany.length === 1) {
    return setsForStoredCompany[0];
  }

  return undefined;
};

export const resolveAuthSetsBySubdomain = ({
  subDomainCompanyIdFromBe,
  authSets,
}: {
  subDomainCompanyIdFromBe: number | undefined;
  authSets: AuthorizationSet[];
}): {
  subdomainAuthSets: AuthorizationSet[];
  subdomainCompanyId: number | undefined;
} => {
  if (subDomainCompanyIdFromBe && subDomainCompanyIdFromBe > 0) {
    const matchesByApiCompanyId = authSets.filter(
      x => x.companyId === subDomainCompanyIdFromBe,
    );

    if (matchesByApiCompanyId.length) {
      return {
        subdomainAuthSets: matchesByApiCompanyId,
        subdomainCompanyId: subDomainCompanyIdFromBe,
      };
    }
  }

  const companySubDomain = getCompanySubDomain()?.toLowerCase();

  if (!companySubDomain) {
    return { subdomainAuthSets: [], subdomainCompanyId: undefined };
  }

  const matchesBySubdomain = authSets.filter(
    x => x.companyCode?.toLowerCase() === companySubDomain,
  );

  return {
    subdomainAuthSets: matchesBySubdomain,
    subdomainCompanyId: matchesBySubdomain[0]?.companyId,
  };
};

/**
 * Returns the companyId of the page the user originally requested before being
 * redirected to /login, read from the `returnTo` localStorage entry written by
 * `ProtectedRoute` / `storeCurrentPageAndRedirectToLogin`. Used to auto-select
 * a matching role after deep-link login so the choose-company dialog can be
 * skipped. The caller is expected to clear `returnTo` after consuming it so
 * later `setOrChooseProfile` runs (e.g. on language change) don't override the
 * user's manual company switch.
 */
export const getCompanyIdFromAppPath = (path: string): number | null => {
  const pathOnly = path.split("?")[0] ?? "";
  const match = pathOnly.match(COMPANY_ID_PATH_REGEX);
  return match ? Number(match[1]) : null;
};

export const getCompanyIdFromLoginReturnTo = (): number | null => {
  const returnTo = localStorageHelpers.getItem<string>("returnTo");
  if (!returnTo || !isValidAppPath(returnTo)) {
    return null;
  }
  return getCompanyIdFromAppPath(returnTo);
};

/**
 * Company we can fetch system settings for before Recoil scope is applied:
 * current `/c/:companyId/` route, post-login `returnTo`, stored auth set, or the
 * only company on the profile.
 */
export const peekKnownSessionCompanyId = ({
  authSets,
}: {
  authSets: AuthorizationSet[];
}): number | undefined => {
  if (!authSets.length) {
    return undefined;
  }

  const hasCompanyAccess = (companyId: number): boolean =>
    authSets.some(authSet => authSet.companyId === companyId);

  const pathCompanyId = getCompanyIdFromAppPath(getCurrentRouterPathname());
  if (pathCompanyId !== null && hasCompanyAccess(pathCompanyId)) {
    return pathCompanyId;
  }

  const returnToCompanyId = getCompanyIdFromLoginReturnTo();
  if (returnToCompanyId !== null && hasCompanyAccess(returnToCompanyId)) {
    return returnToCompanyId;
  }

  const restored = findRestoredAuthSet({
    authSets,
    stored: ProfileStorage.authSet,
  });
  if (restored?.companyId) {
    return restored.companyId;
  }

  if (authSets.length === 1) {
    return authSets[0].companyId;
  }

  return undefined;
};

export const isTestOrDevelopHostOrEnv = (): boolean => {
  if (isLocalOrJenkinsMode()) {
    return true;
  }

  const companySubDomain = getCompanySubDomain();

  if (isReservedNonCompanySubDomain(companySubDomain)) {
    return true;
  }

  return (
    !!companySubDomain &&
    COMPANY_SELECT_ALLOWED_SUBDOMAIN_TOKENS.some(token =>
      companySubDomain.includes(token),
    )
  );
};
